import { RandomSourceWithSeed } from "./random";

// crypto.getRandomValues and Uint8Array work in both Node.js (>=15) and all modern browsers,
// avoiding the need for a require("crypto") call that breaks in Vite's ESM browser context.
const rando = (buf: Uint8Array<ArrayBuffer>): Uint8Array<ArrayBuffer> => crypto.getRandomValues(buf);
const alloc = (size: number): Uint8Array<ArrayBuffer> => new Uint8Array(size);

let pool: Uint8Array<ArrayBuffer>;
let poolOffset: number;

let seededSource: RandomSourceWithSeed | undefined;

/**
 * It is best to make fewer, larger requests to the crypto module to
 * avoid system call overhead. So, random numbers are generated in a
 * pool. The pool is a Buffer that is larger than the initial random
 * request size by a factor of 32. The pool is enlarged if subsequent
 * requests exceed the maximum buffer size.
 */
function random(size: number): Uint8Array {
  // Bypasses the pool rather than swapping the function that fills it. The pool is filled 32 requests
  // at a time and sliced by the calls after, so sharing it would serve leftover crypto bytes on
  // entering a seeded scope and leak seeded bytes to real ids on leaving one.
  if (seededSource) {
    const seededBytes = alloc(size);
    for (let index = 0; index < size; index++) {
      seededBytes[index] = seededSource.nextIntInRange(0, 255);
    }
    return seededBytes;
  }

  if (!pool || pool.length < size) {
    pool = alloc(size * 32);
    rando(pool);
    poolOffset = 0;
  } else if (poolOffset + size > pool.length) {
    rando(pool);
    poolOffset = 0;
  }

  const res = pool.subarray(poolOffset, poolOffset + size);
  poolOffset += size;
  return res;
}

/**
 * This alphabet is drawn from github.com/ai/nanoid and is a reordering of alphanumeric characters
 * that is optimized for better gzip compression
 */
const urlAlphabet = "ModuleSymbhasOwnPro0123456789ABCDEFGHNRVfgctiUvzWKqYTJkLxpZXIjQW";

/**
 * uid produces a random string inspired by github.com/ai/nanoid.
 *
 * In node.js, it utilizes the node.js crypto module and it uses the browser crypto
 * module in browsers. It is made significantly more efficient by making more coarse, pooled requests
 * to these modules.
 */
export default function uid(size: number = 16): string {
  const bytes = random(size);
  let id = "";
  while (size--) {
    id += urlAlphabet[bytes[size] & 63];
  }
  return id;
}

/**
 * Runs `fn` with `uid()` drawing from a PRNG seeded by `seed`, making ids minted inside a pure
 * function of the seed.
 *
 * Synchronous only, enforced by the return type: the override is module-global and is restored as
 * soon as `fn` returns.
 */
export function withSeededUids<T>(seed: number, fn: () => T extends Promise<unknown> ? never : T): T {
  const previousSource = seededSource;
  seededSource = new RandomSourceWithSeed(seed);
  try {
    return fn();
  } finally {
    seededSource = previousSource;
  }
}

export function isUid(id: string): boolean {
  return typeof id === "string" && id.length === 16 && /^[a-zA-Z0-9]+$/.test(id);
}

export function containsUid(str: string): boolean {
  if (typeof str !== "string") {
    return false;
  }

  // Look for any 16-character alphanumeric substring
  const uidPattern = /[a-zA-Z0-9]{16}/;
  return uidPattern.test(str);
}
